import { type Ref, ref } from "vue";

type HttpMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

interface ApiRequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: HttpMethod;
}

export interface ApiClient {
  delete: <ResponseBody>(path: string, options?: ApiRequestOptions) => Promise<ResponseBody>;
  error: Ref<Error | null>;
  get: <ResponseBody>(path: string, options?: ApiRequestOptions) => Promise<ResponseBody>;
  isLoading: Ref<boolean>;
  patch: <ResponseBody>(path: string, body?: unknown, options?: ApiRequestOptions) => Promise<ResponseBody>;
  post: <ResponseBody>(path: string, body?: unknown, options?: ApiRequestOptions) => Promise<ResponseBody>;
  put: <ResponseBody>(path: string, body?: unknown, options?: ApiRequestOptions) => Promise<ResponseBody>;
}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

export function useApi(): ApiClient {
  const error = ref<Error | null>(null);
  const isLoading = ref(false);

  async function _request<ResponseBody>(path: string, options: ApiRequestOptions = {}): Promise<ResponseBody> {
    isLoading.value = true;
    error.value = null;

    try {
      const hasBody = options.body !== undefined;
      const response = await fetch(`${apiBaseUrl}/api${path}`, {
        body: hasBody ? JSON.stringify(options.body) : undefined,
        credentials: apiBaseUrl ? "include" : "same-origin",
        headers: {
          "x-requested-with": "TermineAdmin",
          ...(hasBody
            ? {
                "content-type": "application/json",
              }
            : {}),
          ...options.headers,
        },
        method: options.method ?? "GET",
      });

      if (!response.ok) {
        let serverMessage: string | null = null;
        try {
          const body = (await response.json()) as {
            error?: unknown;
          };
          if (typeof body.error === "string" && body.error.length > 0) {
            serverMessage = body.error;
          }
        } catch {
          /* response body was not JSON */
        }
        throw new Error(serverMessage ?? `Request failed with status ${response.status}`);
      }

      return (await response.json()) as ResponseBody;
    } catch (caughtError) {
      const normalizedError = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      error.value = normalizedError;
      throw normalizedError;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    delete: <ResponseBody>(path: string, options: ApiRequestOptions = {}) =>
      _request<ResponseBody>(path, {
        ...options,
        method: "DELETE",
      }),
    error,
    get: <ResponseBody>(path: string, options: ApiRequestOptions = {}) =>
      _request<ResponseBody>(path, {
        ...options,
        method: "GET",
      }),
    isLoading,
    patch: <ResponseBody>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
      _request<ResponseBody>(path, {
        ...options,
        body,
        method: "PATCH",
      }),
    post: <ResponseBody>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
      _request<ResponseBody>(path, {
        ...options,
        body,
        method: "POST",
      }),
    put: <ResponseBody>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
      _request<ResponseBody>(path, {
        ...options,
        body,
        method: "PUT",
      }),
  };
}
